import React, { useState, useRef } from 'react';
import './EditProfileModal.css';

const EditProfileModal = ({ profile, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    displayName: profile.displayName || '',
    bio: profile.bio || '',
    location: profile.location || '',
    website: profile.website || '',
    bannerImage: profile.bannerImage || '',
    profileImage: profile.profileImage || ''
  });

  const bannerInputRef = useRef(null);
  const profileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        handleInputChange(field, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    const updatedProfile = {
      ...profile,
      ...formData
    };
    onSave(updatedProfile);
  };

  const handleBannerClick = () => {
    bannerInputRef.current?.click();
  };

  const handleProfileClick = () => {
    profileInputRef.current?.click();
  };

  const removeBanner = () => {
    handleInputChange('bannerImage', '');
  };



  return (
    <div className="edit-profile-overlay">
      <div className="edit-profile-modal">
        {/* Header */}
        <div className="edit-profile-header">
          <div className="header-left">
            <button className="close-btn" onClick={onClose}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M10.59 12L4.54 5.96l1.42-1.42L12 10.59l6.04-6.05 1.42 1.42L13.41 12l6.05 6.04-1.42 1.42L12 13.41l-6.04 6.05-1.42-1.42L10.59 12z"/>
              </svg>
            </button>
            <h2>Edit profile</h2>
          </div>
          <button className="save-btn" onClick={handleSave}>
            Save
          </button>
        </div>

        {/* Banner Section */}
        <div className="edit-banner-section">
          <div 
            className="edit-banner"
            style={{
              backgroundImage: formData.bannerImage ? `url(${formData.bannerImage})` : 'none',
              backgroundColor: formData.bannerImage ? 'transparent' : '#536471'
            }}
          >
            <div className="banner-overlay">
              <div className="banner-actions">
                <button className="banner-action-btn" onClick={handleBannerClick}>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M3 5.5C3 4.119 4.119 3 5.5 3h13C19.881 3 21 4.119 21 5.5v13c0 1.381-1.119 2.5-2.5 2.5h-13C4.119 21 3 19.881 3 18.5v-13zM5.5 5c-.276 0-.5.224-.5.5v9.086l3-3 3 3 5-5 3 3V5.5c0-.276-.224-.5-.5-.5h-13zM19 15.414l-3-3-5 5-3-3-3 3V18.5c0 .276.224.5.5.5h13c.276 0 .5-.224.5-.5v-3.086zM9.75 7C8.784 7 8 7.784 8 8.75s.784 1.75 1.75 1.75 1.75-.784 1.75-1.75S10.716 7 9.75 7z"/>
                  </svg>
                </button>
                {formData.bannerImage && (
                  <button className="banner-action-btn" onClick={removeBanner}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                      <path d="M10.59 12L4.54 5.96l1.42-1.42L12 10.59l6.04-6.05 1.42 1.42L13.41 12l6.05 6.04-1.42 1.42L12 13.41l-6.04 6.05-1.42-1.42L10.59 12z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Profile Picture */}
          <div className="edit-profile-avatar">
            <div className="avatar-container">
              {formData.profileImage ? (
                <img src={formData.profileImage} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              )}
              <div className="avatar-overlay">
                <button className="avatar-action-btn" onClick={handleProfileClick}>
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                    <path d="M3 5.5C3 4.119 4.119 3 5.5 3h13C19.881 3 21 4.119 21 5.5v13c0 1.381-1.119 2.5-2.5 2.5h-13C4.119 21 3 19.881 3 18.5v-13zM5.5 5c-.276 0-.5.224-.5.5v9.086l3-3 3 3 5-5 3 3V5.5c0-.276-.224-.5-.5-.5h-13zM19 15.414l-3-3-5 5-3-3-3 3V18.5c0 .276.224.5.5.5h13c.276 0 .5-.224.5-.5v-3.086zM9.75 7C8.784 7 8 7.784 8 8.75s.784 1.75 1.75 1.75 1.75-.784 1.75-1.75S10.716 7 9.75 7z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="edit-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              maxLength={50}
              placeholder="Your display name"
            />
            <div className="char-count">{formData.displayName.length}/50</div>
          </div>

          <div className="form-group">
            <label>Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              maxLength={160}
              placeholder="Tell the cosmos about yourself"
              rows={3}
            />
            <div className="char-count">{formData.bio.length}/160</div>
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              maxLength={30}
              placeholder="Where in the cosmos are you?"
            />
            <div className="char-count">{formData.location.length}/30</div>
          </div>

          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              maxLength={100}
              placeholder="https://your-cosmic-website.com"
            />
            <div className="char-count">{formData.website.length}/100</div>
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handleImageUpload('bannerImage', e.target.files[0])}
        />
        <input
          ref={profileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => handleImageUpload('profileImage', e.target.files[0])}
        />
      </div>
    </div>
  );
};

export default EditProfileModal;
